<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watchEffect} from 'vue'
import {ListDetailLayout} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import PrincipalDetails from './PrincipalDetails.vue'
import CreatePrincipalModal from './CreatePrincipalModal.vue'
import {PrincipalsDataProvider} from './data-provider'
import {useIamEventsStore} from '@/stores/iam/iam-events-store'
import type {PrincipalsListParams} from '@/backend/iam.types'

const context = useContext()
const { iam: iamService } = useBackend()
const t = useLocalization('iam.principals')
const dialogs = useDialogs()
const iamEvents = useIamEventsStore()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('viewTitle')
  }
})

const canRead = computed(() => context.authorize('iam/principals:read'))
const canWrite = computed(() => context.authorize('iam/principals:write'))

const filters = ref<PrincipalsListParams>({
  roles: [],
  includeSuspended: true,
  limit: 100,
})

const principalsProvider = new PrincipalsDataProvider(filters, iamService, t)

const selectedPrincipalId = ref<string | null>(null)

let unsubscribe: (() => void) | null = null

function openCreateModal() {
  dialogs.openModal(t('create.title'), CreatePrincipalModal, true, {
    iamService,
    onCreated: (principal: any) => {
      // Refresh list and select new principal
      principalsProvider.refresh()
      selectedPrincipalId.value = principal.id
    },
    onCancel: () => {
      // Modal will close automatically
    },
  })
}

onMounted(() => {
  if (!canRead.value) {
    context.currentView.value?.close()
  }

  unsubscribe = iamEvents.subscribeToPrincipalsList(() => {
    principalsProvider.refresh()
  })
})

onUnmounted(() => {
  unsubscribe?.()
})
</script>

<template>
  <div class="principals-view">
    <ListDetailLayout
      :provider="principalsProvider"
      :detail-component="PrincipalDetails"
      cache-key="iam-principals"
      :selected="selectedPrincipalId"
      @update:selected="(val: string | null) => selectedPrincipalId = val"
      :allow-multi-select="false"
    >
      <template #list-toolbar>
        <div class="toolbar-content">
          <button v-if="canWrite" @click="openCreateModal" class="btn-primary">
            {{ t('create.button') }}
          </button>
        </div>
      </template>
    </ListDetailLayout>
  </div>
</template>

<style scoped>
.principals-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar-content {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
}
</style>
